import React from 'react';
import Image from 'next/image';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import AddPhotoAlternateOutlined from '@mui/icons-material/AddPhotoAlternateOutlined';
import { useDropzone } from 'react-dropzone';
import { AVATAR_TYPE_AND_SIZE } from '@/constants/profile-builder';
import UploadFileIcon from '@/assets/employer/upload.svg';

interface UploadProps {
  sx?: React.CSSProperties;
  inputSx?: React.CSSProperties | Record<string, React.CSSProperties>;
  className?: string;
  fileSize?: number;
  fileSizeName?: string;
  fileType?: string[];
  label: string | React.ReactNode;
  subLabel?: string | React.ReactNode;
  value: string;
  onChange: (file: File) => void;
  handleError?: (errorText: string) => void;
}

const RenderUpload = (props: { fileSizeName: string; fileType: string[] }) => {
  const { fileType, fileSizeName } = props;
  const isPdf = fileType.includes('application/pdf');

  return (
    <div className="text-[#64748b]">
      <IconButton>
        {isPdf ? (
          <Image src={UploadFileIcon} alt="" />
        ) : (
          <AddPhotoAlternateOutlined
            sx={{ color: '#94a3b8', width: 35, height: 35 }}
          />
        )}
      </IconButton>
      <h1 className="text-lg font-medium text-opacity-90 mt-6">
        {isPdf ? '上传文件' : '上传图片'}
      </h1>
      <p className="text-sm text-opacity-90 mt-1">
        支持{fileType.map((item) => item.split('/')[1]).join('、')}格式
      </p>
      <p className="text-sm text-opacity-90 mt-1">大小不超过 {fileSizeName}</p>
    </div>
  );
};

const RenderView = (props: {
  fileSizeName: string;
  fileType: string[];
  value: string;
}) => {
  const { fileType, fileSizeName, value } = props;

  return (
    <div className="w-full h-full p-3 relative">
      <div className="relative flex items-center justify-center w-full h-full">
        {fileType.includes('application/pdf') ? (
          <iframe src={value + '#toolbar=0'} width="100%" height="320px" />
        ) : (
          <Image
            src={value}
            alt={value}
            fill
            style={{ objectFit: 'contain' }}
          />
        )}
      </div>
      <div className="hidden absolute inset-0 rounded-lg bg-black/60 group-hover:block">
        <Button
          className="rounded-lg"
          variant="contained"
          color="primary"
          sx={{
            marginTop: '156px',
            width: '209px',
            height: '45px',
          }}
        >
          重新上传
        </Button>
        <p className="text-sm text-white text-opacity-80 mt-4">
          支持{fileType.map((item) => item.split('/')[1]).join('、')}格式
        </p>
        <p className="text-sm text-white text-opacity-80 mt-1">
          大小不超过 {fileSizeName}
        </p>
      </div>
    </div>
  );
};

const UploadFile = (props: UploadProps) => {
  const {
    value,
    onChange,
    className = '',
    label,
    sx = {},
    inputSx = {},
    subLabel = '',
    fileSize = AVATAR_TYPE_AND_SIZE.SIZE,
    fileSizeName = AVATAR_TYPE_AND_SIZE.NAME,
    fileType = AVATAR_TYPE_AND_SIZE.TYPE,
    handleError,
  } = props;

  const handleDrop = React.useCallback((acceptedFiles: File[] | undefined) => {
    const file: File | undefined = acceptedFiles && acceptedFiles[0];
    if (file) {
      if (!fileType.includes(file.type)) {
        handleError?.(`抱歉，您上传的${label}格式不符，请重新上传`);
      } else if (file.size > fileSize) {
        handleError?.(
          `抱歉，您上传的${label}大小超过${fileSizeName}，请重新上传`,
        );
      } else {
        onChange(file);
      }
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleDrop,
    multiple: false,
  });

  return (
    <div>
      <Box className={`flex flex-col ${className}`} sx={sx}>
        <div className="mb-3 flex flex-col">
          {label && (
            <label className="text-sm font-semibold">{`请上传${label}`}</label>
          )}
          {subLabel && (
            <label className={`text-xs mt-1 text-gray-600`}>{subLabel}</label>
          )}
        </div>
        <input {...getInputProps()} />
        <Box
          className={`flex justify-center items-center text-center ${className} cursor-pointer group`}
          sx={{
            ...inputSx,
          }}
          {...getRootProps()}
        >
          {value ? (
            <RenderView
              fileType={fileType}
              fileSizeName={fileSizeName}
              value={value}
            />
          ) : (
            <RenderUpload fileType={fileType} fileSizeName={fileSizeName} />
          )}
        </Box>
      </Box>
    </div>
  );
};

export default React.memo(UploadFile);
